import { graphql } from 'gql.tada'
import Router from 'next/router'
import { useSyncExternalStore } from 'react'
import { Client, CombinedError } from 'urql'

import matcher from '@/gql/generated/matcher'

import { SCHOOL_GRAPHQL_ENDPOINT } from './env'
import { flutterReady } from './flutter'
import {
  Authentication,
  clearAuthentication,
  getAuthentication,
  makeClientStore,
  saveAuthentication,
  withKeylessTypes,
} from './urql-common'

function isPresent(auth: Authentication | null): auth is Authentication {
  return auth != null
}

const TokenRefreshMutation = graphql(`
  mutation TokenRefreshMutation($fingerprint: String!, $refreshToken: ID!) {
    tokenRefresh(input: { fingerprint: $fingerprint, refreshToken: $refreshToken }) {
      authentication {
        accessToken
        fingerprint
        refreshToken
      }
    }
  }
`)

export function isContractSignRequiredError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'CONTRACT_SIGN_REQUIRED_ERROR')
}

export function isGuardianTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'GUARDIAN_TOKEN_ON_OTHER_SCHEMA')
}

export function isPartnerTokenError(error: unknown): boolean {
  if (!(error instanceof CombinedError)) return false
  return error.graphQLErrors.some((e) => e.extensions.code == 'PARTNER_TOKEN_ON_OTHER_SCHEMA')
}

function updateSchoolGroupsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const viewerKey = cache.resolve(queryKey, 'viewer')
    if (viewerKey) {
      const schoolKey = cache.resolve(viewerKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'groupsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolGroup')
  }
}

function updateSchoolStudentsPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const viewerKey = cache.resolve(queryKey, 'viewer')
    if (viewerKey) {
      const schoolKey = cache.resolve(viewerKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'studentsPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

function updateSchoolTeachersPagination(cache: any, id?: string) {
  if (id) {
    const queryKey = cache.keyOfEntity({ __typename: 'Query' })
    const viewerKey = cache.resolve(queryKey, 'viewer')
    if (viewerKey) {
      const schoolKey = cache.resolve(viewerKey, 'school')
      if (schoolKey) {
        const paginationFields = cache.inspectFields(schoolKey)
        paginationFields.forEach((field: any) => {
          if (field.fieldName === 'teachersPagination') {
            cache.invalidate(schoolKey, field.fieldName, field.arguments || {})
          }
        })
      }
    }
  } else {
    cache.invalidate('SchoolUser')
  }
}

const { getSnapshot, resetClient, subscribe } = makeClientStore(SCHOOL_GRAPHQL_ENDPOINT, {
  authExchangeOptions: {
    anonymousFields: new Set(['tokenRefresh', '__schema']),
    refreshAuth: async (mutate) => {
      let authentication = getAuthentication()
      if (authentication) {
        const result = await mutate(TokenRefreshMutation, {
          fingerprint: authentication.fingerprint,
          refreshToken: authentication.refreshToken,
        })
        saveAuthentication(result.data?.tokenRefresh?.authentication ?? null)
        authentication = getAuthentication()
      }

      // refresh token successed
      if (isPresent(authentication)) return

      clearAuthentication()
      await logout()
    },
  },
  cacheExchangeOptions: {
    possibleTypes: matcher.possibleTypes,
    updates: {
      Mutation: {
        schoolStudentDelete(result: any, __, cache) {
          const id = result.schoolStudentDelete?.deletedStudentId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolTeacherDelete(result: any, __, cache) {
          const id = result.schoolTeacherDelete?.deletedTeacherId
          if (id) cache.invalidate({ __typename: 'SchoolUser', id })
          else cache.invalidate('SchoolUser')
        },
        schoolGroupDelete(result: any, __, cache) {
          const id = result.schoolGroupDelete?.deletedSchoolGroupId
          updateSchoolGroupsPagination(cache, id)
        },
        schoolGroupCreate(result: any, __, cache) {
          const id = result.schoolGroupCreate?.schoolGroup?.id
          updateSchoolGroupsPagination(cache, id)
        },
        schoolStudentCreate(result: any, __, cache) {
          const id = result.schoolStudentCreate?.student?.id
          updateSchoolStudentsPagination(cache, id)
        },
        schoolTeacherCreate(result: any, __, cache) {
          const id = result.schoolTeacherCreate?.teacher?.id
          updateSchoolTeachersPagination(cache, id)
        },
      },
    },
    keys: withKeylessTypes([
      'AssessmentScore',
      'LearningStatusHeatmap',
      'LearningTimeTransition',
      'LearningTimeVisualization',
      'LearningTimeData',
      'ScoreHistory',
      'AssessmentScoreHistory',
      'PlayerAvatar',
      'MotivationPointVisualization',
    ]),
  },
  isErrorHandledInBoundary: (error: unknown) =>
    isContractSignRequiredError(error) || isGuardianTokenError(error) || isPartnerTokenError(error),
})

export async function logout(redirectUrl?: string) {
  clearAuthentication()

  await Router.push(redirectUrl ?? '/web/sign_in')
  resetClient()
}

export function resetUrqlClient() {
  resetClient()
}

export function useUrqlClient(): Client {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export function getCurrentClient(): Client {
  return getSnapshot()
}
